/**
 * iter_212 — Admin Notifications Health & Diagnostics
 *
 * Lists recent notifications with created_at, type, recipient, read
 * state, in-app delivery timing, and push status. Push provider is not
 * wired yet, so push_status is "not_attempted" for all rows. Device
 * tokens and provider secrets are NEVER exposed.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import SharedNavigation from "@/components/SharedNavigation";

const fmtAge = (sec) => {
  if (sec == null) return "—";
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h`;
  return `${Math.round(sec / 86400)}d`;
};

const AdminNotificationsHealthPage = () => {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [health, setHealth] = useState(null);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [targetUser, setTargetUser] = useState("");
  const [since, setSince] = useState(1440); // 24h default

  // iter_212 — Admin guard mirrors AdminControlCenterPage.
  useEffect(() => {
    let cancelled = false;
    api.get("/auth/me")
      .then((r) => {
        if (cancelled) return;
        const u = r.data;
        const isAdmin =
          u?.role === "admin" ||
          u?.role === "moderator" ||
          (u?.badges || []).includes("admin");
        if (!isAdmin) {
          toast.error("Admin access only");
          navigate("/home", { replace: true });
          return;
        }
        setAuthChecked(true);
      })
      .catch(() => {
        if (cancelled) return;
        toast.error("Sign in as an admin");
        navigate("/home", { replace: true });
      });
    return () => { cancelled = true; };
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100, skip: 0 };
      if (type) params.notification_type = type;
      if (targetUser) params.target_user_id = targetUser;
      if (since) params.since_minutes = since;
      const [h, d] = await Promise.all([
        api.get("/admin/notifications/health"),
        api.get("/admin/notifications/diagnostics", { params }),
      ]);
      setHealth(h.data || null);
      setRows(d.data?.rows || []);
      setTotal(d.data?.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load diagnostics");
    } finally {
      setLoading(false);
    }
  }, [type, targetUser, since]);

  useEffect(() => { if (authChecked) load(); }, [load, authChecked]);

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-black text-white">
        <SharedNavigation />
        <p className="pt-24 px-4 text-gray-500 text-sm" data-testid="admin-notif-auth-check">
          Checking permissions…
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="admin-notif-health-page">
      <SharedNavigation />
      <div className="max-w-6xl mx-auto px-4 pt-24 pb-8 space-y-6">
        <div>
          <p className="text-[11px] uppercase tracking-widest text-purple-300 font-bold">Admin</p>
          <h1 className="text-3xl font-black">🔔 Notification Diagnostics</h1>
          <p className="text-sm text-gray-400 mt-1 max-w-2xl">
            Read-only view of recent notifications. Use this to diagnose late,
            missing or misrouted messages. Device tokens and provider secrets
            are never shown.
          </p>
        </div>

        {/* Health summary */}
        {health && (
          <section
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-4"
            data-testid="notif-health-summary"
          >
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-black">Last 24h — volume + read rate</h2>
              <span className="text-[10px] text-gray-500">{health.as_of}</span>
            </div>
            {(health.by_type || []).length === 0 ? (
              <p className="text-sm text-gray-500" data-testid="notif-health-empty">
                No notifications in the last 24h.
              </p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {(health.by_type || []).map((r) => (
                  <div
                    key={r.type}
                    className="rounded-lg bg-black/40 border border-white/10 p-2"
                    data-testid={`notif-health-type-${r.type}`}
                  >
                    <p className="text-[10px] uppercase tracking-widest text-gray-400 truncate">
                      {r.type}
                    </p>
                    <p className="text-white font-black text-lg">
                      {r.count.toLocaleString()}
                    </p>
                    <p className="text-[11px] text-emerald-200">
                      {r.read_pct}% read
                    </p>
                    {r.category && (
                      <p className="text-[10px] text-gray-500 truncate">· {r.category}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* Filters */}
        <section className="flex flex-wrap gap-2 items-end" data-testid="notif-diag-filters">
          <div className="flex-1 min-w-[180px]">
            <label className="text-[10px] uppercase tracking-widest text-gray-400">Type</label>
            <Input
              value={type}
              onChange={(e) => setType(e.target.value)}
              placeholder="e.g. reward, crew_mention"
              className="bg-white/5 border-white/10 text-white h-9"
              data-testid="notif-diag-filter-type"
            />
          </div>
          <div className="flex-1 min-w-[180px]">
            <label className="text-[10px] uppercase tracking-widest text-gray-400">Recipient user_id</label>
            <Input
              value={targetUser}
              onChange={(e) => setTargetUser(e.target.value)}
              placeholder="user_abc123"
              className="bg-white/5 border-white/10 text-white h-9"
              data-testid="notif-diag-filter-user"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-400">Window</label>
            <select
              value={since}
              onChange={(e) => setSince(Number(e.target.value))}
              className="h-9 rounded-md bg-white/5 border border-white/10 text-white px-3 text-sm block"
              data-testid="notif-diag-filter-since"
            >
              <option value={60} className="bg-[#13131a]">Last 1h</option>
              <option value={360} className="bg-[#13131a]">Last 6h</option>
              <option value={1440} className="bg-[#13131a]">Last 24h</option>
              <option value={10080} className="bg-[#13131a]">Last 7d</option>
            </select>
          </div>
          <Button
            onClick={load}
            disabled={loading}
            className="bg-white text-black hover:bg-amber-200 font-black h-9"
            data-testid="notif-diag-refresh"
          >
            {loading ? "Loading…" : "Refresh"}
          </Button>
        </section>

        {/* Rows */}
        <section data-testid="notif-diag-table-wrap">
          <p className="text-[11px] text-gray-500 mb-2">
            Showing {rows.length} of {total.toLocaleString()} notification(s)
          </p>
          {loading ? (
            <p className="text-gray-500 text-sm" data-testid="notif-diag-loading">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-gray-500 text-sm" data-testid="notif-diag-empty">
              No notifications match the current filters.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="notif-diag-table">
                <thead className="text-left text-gray-400 uppercase tracking-widest text-[10px] border-b border-white/10">
                  <tr>
                    <th className="py-2 pr-3">Created</th>
                    <th className="py-2 pr-3">Age</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Category</th>
                    <th className="py-2 pr-3">Recipient</th>
                    <th className="py-2 pr-3">Title</th>
                    <th className="py-2 pr-3">In-app read</th>
                    <th className="py-2 pr-3">Push</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr
                      key={r.notification_id}
                      className="border-b border-white/5 align-top"
                      data-testid={`notif-diag-row-${r.notification_id}`}
                    >
                      <td className="py-2 pr-3 text-gray-300 whitespace-nowrap">
                        {(r.created_at || "").replace("T", " ").slice(0, 19)}
                      </td>
                      <td className="py-2 pr-3 text-amber-200">{fmtAge(r.age_seconds)}</td>
                      <td className="py-2 pr-3 text-white font-mono">{r.type}</td>
                      <td className="py-2 pr-3 text-gray-400">{r.category || "—"}</td>
                      <td className="py-2 pr-3 text-gray-300 font-mono truncate max-w-[140px]">{r.user_id}</td>
                      <td className="py-2 pr-3 text-gray-200 truncate max-w-[220px]" title={r.title}>{r.title}</td>
                      <td className="py-2 pr-3">
                        {r.is_read ? (
                          <span className="text-emerald-300">✓ read</span>
                        ) : r.hidden ? (
                          <span className="text-gray-500">hidden</span>
                        ) : (
                          <span className="text-amber-300">unread</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-gray-500">{r.push_status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminNotificationsHealthPage;
